import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Lock, ServerCog, ShieldCheck, Landmark } from "lucide-react";
import { TurnstileScene } from "@/components/three/TurnstileScene";
import { SectionHeading } from "@/components/SectionHeading";

const CREDS = [
  {
    icon: ShieldCheck,
    title: "Air-gapped AI",
    body: "AI-empowered embedded devices that run fully offline — inference on the device, zero network dependency, nothing leaving the boundary.",
  },
  {
    icon: Lock,
    title: "Hardened by default",
    body: "Signed device identity, allow-listed surfaces, no open tunnels, disciplined secrets. Security is the starting posture, not a later pass.",
  },
  {
    icon: ServerCog,
    title: "Self-hosted inference",
    body: "When sovereignty and latency demand it, models run on your own silicon — your data never leaves the box, and there is no third-party API in the path.",
  },
  {
    icon: Landmark,
    title: "Built for government use",
    body: "A background designing and building systems to the standard government deployment demands — where 'it usually works' is not an acceptable answer.",
  },
];

export function Sovereign() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "240px" });

  return (
    <section id="sovereign" className="relative px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          index="02"
          kicker="Sovereign Systems"
          title={
            <>
              Air-gapped by design. <span className="text-gradient-signal">Yours to control.</span>
            </>
          }
          blurb="Some systems can't phone home. I build AI that operates on the wrong side of the air gap — and infrastructure where data crosses the boundary only on your terms."
        />

        <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-[1fr_0.9fr]">
          {/* credibility grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
            className="grid gap-4 sm:grid-cols-2"
          >
            {CREDS.map((c) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                  }}
                  className="panel rounded-[var(--radius-lg)] p-5"
                >
                  <Icon className="size-5 text-invert-400" />
                  <h3 className="mt-4 font-display text-base font-semibold text-ink-50">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-400">{c.body}</p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* turnstile visual */}
          <div
            ref={ref}
            className="brackets scanlines relative min-h-[420px] overflow-hidden rounded-[var(--radius-lg)] border border-ink-800 bg-ink-950"
          >
            {inView && <TurnstileScene className="!absolute !inset-0 !h-full !w-full" />}
            {/* vignette */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 35%, rgba(5,7,14,0.85) 100%)",
              }}
            />
            <div className="pointer-events-none absolute left-4 top-4 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-ink-500">
              Entropy Gate <span className="text-ink-700">//</span> Air-Gap
            </div>
            <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[0.6rem] uppercase tracking-[0.16em]">
              <span className="text-signal-400">▶ forward</span>
              <span className="text-ink-600">boundary enforced</span>
              <span className="text-invert-400">inverted ◀</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
